import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { Observable, of, EMPTY, combineLatest, throwError, pipe } from 'rxjs';
import {
  mergeMap,
  catchError,
  map,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  exhaustMap,
  tap,
  concatMap,
  startWith,
  take,
  withLatestFrom,
  finalize,
} from 'rxjs/operators';

import * as fromActions from './contactperson.actions';
import {
  selectApplicationGroups,
  setUseraccess,
  setGroupUseraccess,
  deleteGroupUseraccess,
  deleteGroupUseraccessSuccess,
  initAuthorization,
  setApplications,
  setApplicationGroups,
  setRoles,
  setAccessRules,
  setProfileId,
} from './contactperson.actions';
import {
  selectUnselectedApplicationGroups,
  selectApplicationsForGroup,
  selectAccessRulesForGroup,
  selectProfileId,
} from './contactperson.selectors';
import { extractValidationErrors } from '../../shared/helper.functions';
import { Customer, CustomerLocation, SiteState } from '../../shared/models/shared.model';
import { CustomerSearchService } from '../../shared/services/customer-search.service';
import { Contactperson, AccessRule, Profile } from '../models/contactperson.model';
import { AuthorizationService } from '../services/authorization.service';
import { ContactpersonService } from '../services/contactperson.service';

@Injectable()
export class ContactpersonEffects {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly localeService = inject(LocaleService);
  private readonly customerSearchService = inject(CustomerSearchService);
  private readonly contactpersonService = inject(ContactpersonService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialogService = inject(DialogService);

  setContactPersonId$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.setSelectedContactpersonId),
      pipe(
        mergeMap(({ contactpersonId }) =>
          this.contactpersonService.getContactpersonById(contactpersonId).pipe(
            map((contactperson: Contactperson) =>
              fromActions.setSelectedContactperson({ contactperson }),
            ),
            catchError((error: Error) => of(fromActions.getContactpersonsError({ error }))),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
        ),
      ),
    ),
  );

  searchContactpersons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.searchContactpersons),
      debounceTime(400),
      map(({ searchtext }) => (typeof searchtext === 'string' ? searchtext : undefined)),
      distinctUntilChanged(),
      switchMap((searchtext) =>
        searchtext !== undefined
          ? of(fromActions.getContactpersonsRequest({ searchtext }))
          : of(fromActions.clearContactpersons()),
      ),
    ),
  );

  getContactpersonsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getContactpersonsRequest),
      mergeMap(({ searchtext }) =>
        this.contactpersonService.getContactpersons(searchtext).pipe(
          map((contactPersons: Contactperson[]) =>
            fromActions.getContactpersonsSuccess({ contactPersons }),
          ),
          catchError((error: Error) => of(fromActions.getContactpersonsError({ error }))),
        ),
      ),
    ),
  );

  getContactpersonError$: Observable<Action> = this.handleActionError(
    fromActions.getContactpersonsError,
  );

  upsertContactpersonRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.upsertContactpersonRequest),
      exhaustMap(({ contactperson }) =>
        this.contactpersonService.upsertContactperson(contactperson).pipe(
          map((response: { id: number }) =>
            fromActions.upsertContactpersonSuccess({
              contactperson: { ...contactperson, id: response.id },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              fromActions.upsertContactpersonError({ error }),
              fromActions.loadContactpersonsRequest(),
              NotificationActions.errorSnackbar({ message: extractValidationErrors(error) }),
            ),
          ),
        ),
      ),
    ),
  );

  upsertContactPersonSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.upsertContactpersonSuccess),
      tap(async (_) => this.ngZone.run(() => this.router.navigate([`/contactperson/`])).then()),
      switchMap((_) =>
        of(
          NotificationActions.successSnackbar({
            message: this.localeService.translate('app.successmsg.upsert_success'),
          }),
        ),
      ),
    ),
  );

  searchCustomersforRelation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.searchCustomersforRelation),
      debounceTime(400),
      map(({ searchtext }) => (typeof searchtext === 'string' ? searchtext : undefined)),
      distinctUntilChanged(),
      switchMap((searchtext) =>
        searchtext
          ? of(fromActions.getCustomersforSearchboxRequest({ searchtext }))
          : of(fromActions.clearCustomerRelationSearchbox()),
      ),
    ),
  );

  getCustomersforSearchboxRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getCustomersforSearchboxRequest),
      mergeMap(({ searchtext }) =>
        this.customerSearchService.searchCustomers(searchtext).pipe(
          map((customers: Customer[]) =>
            fromActions.getCustomersforSearchboxSuccess({ customers }),
          ),
          catchError((error: Error) => of(fromActions.getCustomersforSearchboxError({ error }))),
        ),
      ),
    ),
  );

  getCustomerRelationsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getCustomerRelationsRequest),
      mergeMap(({ id }) =>
        this.contactpersonService.getCustomerRelationsForContactperson(id).pipe(
          map((customers: Customer[]) => fromActions.getCustomerRelationsSuccess({ customers })),
          catchError((error: Error) => of(fromActions.getCustomerRelationsError({ error }))),
        ),
      ),
    ),
  );

  getLocationsForRelationWithCustomerRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getLocationsForRelationWithCustomerRequest),
      mergeMap(({ customerId, contactpersonId }) =>
        this.contactpersonService
          .getLocationsForRelationWithCustomer(customerId, contactpersonId)
          .pipe(
            map((locations: CustomerLocation[]) =>
              fromActions.getLocationsForRelationWithCustomerSuccess({ locations }),
            ),
            catchError((error: Error) =>
              of(fromActions.getLocationsForRelationWithCustomerError({ error })),
            ),
          ),
      ),
    ),
  );

  getLocationsForRelationWithCustomerError$: Observable<Action> = this.handleActionError(
    fromActions.getLocationsForRelationWithCustomerError,
  );

  setRelationWithLocationRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.setRelationWithLocationRequest),
      mergeMap(({ customerId, contactpersonId, locationIds }) =>
        this.contactpersonService
          .setRelationWithLocationRequest(customerId, contactpersonId, locationIds)
          .pipe(
            concatMap(() => [
              NotificationActions.successSnackbar({
                message: this.localeService.translate('app.successmsg.upsert_success'),
              }),
              fromActions.getLocationsForRelationWithCustomerRequest({
                customerId,
                contactpersonId,
              }),
            ]),
            catchError((error: Error) => of(fromActions.setRelationWithLocationError({ error }))),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  initAuthorization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initAuthorization),
      switchMap(({ externalId }) =>
        combineLatest([
          this.authorizationService.getApplications(),
          this.authorizationService.getApplicationGroups(),
          this.authorizationService.getRoles(),
          this.authorizationService
            .getProfileId(externalId, 'CustomerAccountManagementService', 'ContactPerson')
            .pipe(
              catchError((error: HttpErrorResponse) => {
                if (error?.status === 404) {
                  return throwError(
                    new Error(
                      this.localeService.translate(
                        'app.contactperson.authorization.contactperson_employee_not_found',
                      ),
                    ),
                  );
                }
              }),
              switchMap((profile) =>
                this.authorizationService
                  .getUseraccess(profile.profileId)
                  .pipe(
                    map(
                      (accessRules: AccessRule[]) =>
                        [profile, accessRules] as [Profile, AccessRule[]],
                    ),
                  ),
              ),
            ),
        ]).pipe(
          concatMap(([applications, applicationGroups, roles, [profile, accessRules]]) => [
            setApplications({ applications }),
            setApplicationGroups({ applicationGroups }),
            setRoles({ roles }),
            setAccessRules({ accessRules }),
            setProfileId({ profileId: profile.profileId }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError((error: Error) =>
            of(
              CoreActions.setLoading({ loading: false }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  selectApplicationGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(selectApplicationGroups),
      withLatestFrom(this.store.select(selectUnselectedApplicationGroups)),
      switchMap(([_, unselectedApplicationGroups]) =>
        this.dialogService.multiselect({
          title: this.localeService.translate(
            'app.contactperson.authorization.add_applicationgroup',
          ),
          selected: [],
          emptyText: '',
          options: unselectedApplicationGroups.map((applicationGroup) => ({
            value: applicationGroup,
            name: applicationGroup.displayName,
          })),
        }),
      ),
      concatMap(({ status, result }) =>
        status === DIALOG_RETURN_TYPES.confirm
          ? result.map((applicationGroup) => setGroupUseraccess({ groupId: applicationGroup.id }))
          : EMPTY,
      ),
    ),
  );

  setUseraccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setUseraccess),
      withLatestFrom(this.store.select(selectProfileId)),
      switchMap(([action, profileId]: [{ applicationKey: string; roleKey: string }, number]) =>
        this.authorizationService
          .setUseraccess(profileId, action.applicationKey, action.roleKey)
          .pipe(
            concatMap((_) => [
              NotificationActions.successSnackbar({
                message: this.localeService.translate('app.successmsg.upsert_success'),
              }),
              CoreActions.setLoading({ loading: false }),
            ]),
            startWith(CoreActions.setLoading({ loading: true })),
          ),
      ),
      catchError((error: Error) =>
        of(NotificationActions.errorSnackbar({ message: error.message })),
      ),
    ),
  );

  setGroupUseraccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setGroupUseraccess),
      withLatestFrom(this.store.select(selectProfileId)),
      switchMap(([action, profileId]: [{ groupId: number }, number]) =>
        this.store.pipe(
          select(selectApplicationsForGroup, action.groupId),
          take(1),
          switchMap((applications) =>
            combineLatest(
              applications.map((application) =>
                this.authorizationService.setUseraccess(
                  profileId,
                  application.key,
                  application.defaultRoleKey,
                ),
              ) as [],
            ),
          ),
          concatMap((_) => [
            NotificationActions.successSnackbar({
              message: this.localeService.translate('app.successmsg.upsert_success'),
            }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
      catchError((error: Error) =>
        of(NotificationActions.errorSnackbar({ message: error.message })),
      ),
    ),
  );

  deleteGroupUseraccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteGroupUseraccess),
      switchMap((action: { groupId: number }) =>
        this.store.pipe(
          select(selectAccessRulesForGroup, action.groupId),
          take(1),
          map((oldAccessRules) => [oldAccessRules, action.groupId]),
        ),
      ),
      withLatestFrom(this.store.select(selectProfileId)),
      switchMap(([[oldAccessRules, groupId], profileId]: [[AccessRule[], number], number]) =>
        combineLatest(
          oldAccessRules.map((accessRule) =>
            this.authorizationService.deleteUseraccess(profileId, accessRule.applicationKey).pipe(
              catchError((error: HttpErrorResponse) => {
                if (error?.status !== 404) {
                  return of(NotificationActions.errorSnackbar({ message: error.message }));
                }
                return of(true);
              }),
            ),
          ),
        ).pipe(
          concatMap((_) => [
            deleteGroupUseraccessSuccess({ groupId }),
            NotificationActions.successSnackbar({
              message: this.localeService.translate('app.successmsg.upsert_success'),
            }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  private handleActionError(handleAction: Action) {
    return createEffect(() =>
      this.actions$.pipe(
        ofType(handleAction.type),
        map((action: Action & { error?: number }) =>
          Number(action?.error) === 404
            ? this.localeService.translate('app.errormsg.not_found')
            : this.localeService.translate('app.errormsg.not_loaded'),
        ),
        switchMap((result) => of(NotificationActions.errorSnackbar({ message: result }))),
      ),
    );
  }
}
